import axios from 'axios';
import { ApiError } from './ApiError';

export type ExpectedErrorCategory = 'auth' | 'network';

const AUTH_STATUSES = new Set([401, 403]);

// Browser wording for a dropped/unreachable connection: Chrome ("Failed to fetch", "network error"
// for an interrupted stream), Safari ("Load failed"), Firefox ("NetworkError when attempting…").
const NETWORK_MESSAGE = /network ?error|failed to fetch|load failed/i;

const NETWORK_AXIOS_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED']);

/**
 * Classifies errors caused by the user's session or connection rather than by our code:
 * an expired/insufficient session (401/403) or no network. Returns null for anything else,
 * which should still be reported.
 */
export const expectedErrorCategory = (error: unknown): ExpectedErrorCategory | null => {
  if (error instanceof ApiError) {
    if (error.status === null) return 'network';
    return AUTH_STATUSES.has(error.status) ? 'auth' : null;
  }
  if (axios.isAxiosError(error)) {
    if (error.response) return AUTH_STATUSES.has(error.response.status) ? 'auth' : null;
    return error.code && NETWORK_AXIOS_CODES.has(error.code) ? 'network' : null;
  }
  if (error instanceof TypeError && NETWORK_MESSAGE.test(error.message)) return 'network';
  return null;
};

export const isExpectedError = (error: unknown): boolean => expectedErrorCategory(error) !== null;
